import { useEffect, useRef, useState } from "react";
import axios from "axios";
import useEmblaCarousel from "embla-carousel-react";
import { Reveal } from "@/lib/motion";
import { ArrowUpRight, ArrowLeft, ArrowRight } from "lucide-react";
import { useSiteContent } from "@/lib/useSiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const availabilityStyle = (a) => {
  switch ((a || "").toLowerCase()) {
    case "available":
      return "bg-[#B68D40] text-white";
    case "reserved":
      return "bg-[#1D1D1F] text-white";
    case "sold":
      return "bg-white text-[#1D1D1F] border border-[#1D1D1F]/20";
    case "coming soon":
      return "bg-white/90 text-[#1D1D1F] border border-[#B68D40]/50";
    default:
      return "bg-[#1D1D1F]/70 text-white";
  }
};

export default function FeaturedOpportunities({ onRequestDetails }) {
  const oc = useSiteContent().opportunities;
  const [properties, setProperties] = useState([]);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/properties`)
      .then((r) => setProperties(r.data || []))
      .catch(() => setProperties([]));
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();
  }, [emblaApi, properties]);

  return (
    <section
      id="opportunities"
      data-testid="opportunities-section"
      className="section-pad bg-white"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Reveal><p className="eyebrow">{oc.eyebrow}</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif-luxe text-[#1D1D1F] text-4xl md:text-5xl leading-[1.08] mt-5 max-w-2xl">
                {oc.heading}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="flex md:items-end md:justify-between gap-6">
              <p className="text-[#616161] max-w-md text-sm md:text-base leading-relaxed">
                {oc.intro}
              </p>
              <div className="hidden md:flex gap-2 shrink-0">
                <button
                  aria-label="Previous"
                  data-testid="opps-prev"
                  disabled={!canPrev}
                  onClick={() => emblaApi?.scrollPrev()}
                  className={`w-11 h-11 flex items-center justify-center border transition-all ${
                    canPrev
                      ? "border-[#1D1D1F] text-[#1D1D1F] hover:bg-[#1D1D1F] hover:text-white"
                      : "border-black/15 text-black/25 cursor-not-allowed"
                  }`}
                >
                  <ArrowLeft size={16} strokeWidth={1.4} />
                </button>
                <button
                  aria-label="Next"
                  data-testid="opps-next"
                  disabled={!canNext}
                  onClick={() => emblaApi?.scrollNext()}
                  className={`w-11 h-11 flex items-center justify-center border transition-all ${
                    canNext
                      ? "border-[#1D1D1F] text-[#1D1D1F] hover:bg-[#1D1D1F] hover:text-white"
                      : "border-black/15 text-black/25 cursor-not-allowed"
                  }`}
                >
                  <ArrowRight size={16} strokeWidth={1.4} />
                </button>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 overflow-hidden -mx-6 md:-mx-10" ref={emblaRef} data-testid="opps-carousel">
          <div className="flex">
            {properties.map((o, i) => (
              <div
                key={o.id}
                className="min-w-0 shrink-0 grow-0 basis-[85%] sm:basis-[65%] md:basis-[46%] lg:basis-[32%] px-6 md:px-5 first:pl-6 md:first:pl-10 last:pr-6 md:last:pr-10"
                data-testid={`opportunity-card-${i}`}
              >
                <div className="bg-white group h-full flex flex-col">
                  <div className="relative overflow-hidden">
                    <img
                      src={o.image_url}
                      alt={o.name}
                      className="w-full h-[360px] md:h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute top-6 left-6 bg-[#1D1D1F]/70 backdrop-blur-md text-white text-[0.7rem] tracking-[0.14em] uppercase px-4 py-2">
                      {o.developer}
                    </div>
                    <div
                      data-testid={`opportunity-avail-${i}`}
                      className={`absolute top-6 right-6 text-[0.65rem] tracking-[0.16em] uppercase px-3 py-1.5 ${availabilityStyle(o.availability)}`}
                    >
                      {o.availability}
                    </div>
                  </div>

                  <div className="pt-8 flex-1 flex flex-col">
                    <h3 className="font-serif-luxe text-2xl md:text-3xl text-[#1D1D1F] leading-tight">{o.name}</h3>

                    <div className="grid grid-cols-3 gap-4 mt-5 border-t border-b border-black/10 py-4">
                      <div>
                        <div className="text-[0.65rem] uppercase tracking-[0.14em] text-[#616161]">Starting</div>
                        <div className="text-[#1D1D1F] font-medium mt-1 text-sm">{o.starting_price}</div>
                      </div>
                      <div>
                        <div className="text-[0.65rem] uppercase tracking-[0.14em] text-[#616161]">Payment</div>
                        <div className="text-[#1D1D1F] font-medium mt-1 text-sm">{o.payment_plan}</div>
                      </div>
                      <div>
                        <div className="text-[0.65rem] uppercase tracking-[0.14em] text-[#616161]">Handover</div>
                        <div className="text-[#1D1D1F] font-medium mt-1 text-sm">{o.completion}</div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2 text-sm text-[#616161] flex-1">
                      <p><span className="text-[#1D1D1F] font-medium">Suitable for · </span>{o.audience}</p>
                      <p><span className="text-[#1D1D1F] font-medium">Investment · </span>{o.investment_highlights}</p>
                      <p><span className="text-[#1D1D1F] font-medium">Offers · </span>{o.lifestyle_highlights}</p>
                    </div>

                    <button
                      type="button"
                      onClick={() => onRequestDetails?.(o)}
                      data-testid={`opportunity-details-btn-${i}`}
                      className="mt-6 inline-flex items-center gap-2 text-[#1D1D1F] link-gold text-[0.78rem] tracking-[0.16em] uppercase self-start"
                    >
                      {oc.cta_label || "Request More Details"} <ArrowUpRight size={16} strokeWidth={1.4} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {properties.length === 0 && (
          <p className="text-center text-[#616161] mt-16" data-testid="opps-empty">
            No opportunities currently listed. Please check back soon.
          </p>
        )}
      </div>
    </section>
  );
}
